import { OnlineTemplate } from "~/entities/templateEntities";

/**
 * Whether an organisation gets the unified store finder, and whether that
 * finder may search by delivery address.
 *
 * **Fails closed, on purpose.** `storesScreen.finder` arrives from the template
 * document. A template written before the field existed decodes with the key
 * absent, and that has to read as *not enabled* — never as "no opinion, let
 * them in". The alternative is a switch that does nothing, which is how the QR
 * surface came to be live at every venue on the platform behind a merchant
 * toggle the backend was silently dropping.
 *
 * Strict `=== true` for the same reason: the value crosses Mongo and a JSON
 * boundary before it reaches here, and the string `"false"` is truthy.
 *
 * Pure functions with no context read of their own, so the rule can be asserted
 * without rendering anything.
 */

/** The shape this module needs off the template. Structural rather than
 *  importing the full `OnlineTemplate`, so it can also answer for a payload
 *  that predates `finder` entirely. */
export interface StoreFinderTemplateFlags {
  storesScreen?: {
    finder?: { enabled?: boolean; deliverySearch?: boolean } | null;
  } | null;
}

/**
 * Whether `/sites` and the storefront header serve the unified finder.
 *
 * When false, everything renders exactly as it does today: `SitesList` on
 * `/sites`, the store/fulfilment/ETA row in `InfoBar`, and the existing
 * change-store modal.
 */
export const storeFinderEnabled = (
  template: StoreFinderTemplateFlags | OnlineTemplate | null | undefined,
) =>
  (template as StoreFinderTemplateFlags)?.storesScreen?.finder?.enabled ===
  true;

/**
 * Whether the finder offers the delivery-address branch.
 *
 * Gated behind `storeFinderEnabled` as well as its own flag: a delivery search
 * with no finder to render it in is not a reachable state, and treating the two
 * flags as independent would let a half-configured template put the delivery
 * toggle on a screen that has no finder.
 */
export const deliverySearchEnabled = (
  template: StoreFinderTemplateFlags | OnlineTemplate | null | undefined,
) =>
  storeFinderEnabled(template) &&
  (template as StoreFinderTemplateFlags)?.storesScreen?.finder
    ?.deliverySearch === true;
